// booking/components/BookTickets.tsx
'use client'
import React, { useEffect, useMemo } from 'react'
import cn from 'classnames'
import posthog from 'posthog-js'
import styles from './index.module.scss'

import ExtrasSelection from './stages/ExtrasSelection'
import QuantitySelection from './stages/QuantitySelection'
import DateAndTime from './stages/DateAndTime'
import AddonsSelection from './stages/AddonsSelection'
import CustomerDetails from './stages/CustomerDetails'
import PaymentProcess from './stages/PaymentProcess'
import ThankYou from './stages/ThankYou'
import Associate from './stages/Associate'

import { useSearchParams } from 'next/navigation'

import { BookingProvider, useBookingActionsCtx, useBookingState } from './state/booking.context'
import { BookingExternalContext } from './state/external.context'
import BookingNav from './components/BookingNav'
import PresaleNotice from './components/PresaleNotice'
import PresaleTestBanner from './components/PresaleTestBanner'
import { usePresaleStatus } from './hooks/usePresaleStatus'
import { resolveBookingConfig, stageIdOf, stageNumberOf } from './lib/bookingConfig'
import type { BookingConfig, BookingContextKey, StageId } from './types/bookingConfig'
import { GlobalData } from '.'

const STAGE_COMPONENTS: Record<StageId, React.ReactNode> = {
  voucher: <></>,
  extras: <ExtrasSelection />,
  quantity: <QuantitySelection />,
  datetime: <DateAndTime />,
  addons: <AddonsSelection />,
  customer: <CustomerDetails />,
  payment: <PaymentProcess />,
  thankyou: <ThankYou />,
  associate: <Associate />,
}

export default function BookTicketsPage({ data }: { data: GlobalData }) {
  const searchParams = useSearchParams()

  const flowParam = useMemo(() => {
    const FLOW_ALIASES: Record<string, string> = {
      rir: 'rock-in-rio',
    }
    const paramFlow = searchParams.get('flow') as BookingContextKey | null
    const subdomain = typeof window !== 'undefined' ? window?.location.hostname.split('.')[0] : ''
    const rawFlow = paramFlow ?? subdomain
    console.log('flow param inside')
    console.log(FLOW_ALIASES[rawFlow] ?? rawFlow)
    return FLOW_ALIASES[rawFlow] ?? rawFlow
  }, [searchParams])

  console.log('flow param outside')
  console.log(flowParam)
  const bookingConfig = resolveBookingConfig(flowParam)

  useEffect(() => {
    // Tag every subsequent PostHog event in this session with the active booking
    // flow, so the conversion funnel can be filtered/broken down by flow (e.g.
    // default vs rock-in-rio vs voucher) instead of conflating them together.
    posthog.register({ flow: bookingConfig.context })
    posthog.capture('booking_started', { flow: bookingConfig.context })
  }, [bookingConfig.context])

  return (
    <BookingExternalContext.Provider value={data ?? null}>
      <BookingProvider bookingConfig={bookingConfig}>
        <PresaleTestBanner />
        <BookTicketsInner bookingConfig={bookingConfig} />
      </BookingProvider>
    </BookingExternalContext.Provider>
  )
}

function BookTicketsInner({ bookingConfig }: { bookingConfig: BookingConfig }) {
  const state = useBookingState()
  const actions = useBookingActionsCtx()
  const searchParams = useSearchParams()
  const presaleStatus = usePresaleStatus(bookingConfig.presale)

  const stage = state.stage ?? stageNumberOf(bookingConfig, bookingConfig.stages[0])

  if (state.error && state.error.level === 'high') {
    return <div className={cn(styles.error)}>{state.error.message}</div>
  }

  // A pre-sale link only sells inside its window; outside it, it says when sales
  // open, or from general sale sends people to the normal flow. A booking already
  // underway (cart made, or back from payment) can finish.
  // The status is null while hydrating: the static HTML holds the normal flow (it
  // can't see ?flow=), so the flow renders until the notice replaces it. Rendering
  // anything else then leaves that HTML behind, as React 19 keeps unknown <body> children.
  if (bookingConfig.presale && presaleStatus) {
    const underway = Boolean(state.cart || searchParams.get('cartId'))
    if (presaleStatus !== 'open' && !underway) {
      return (
        <div className={styles.book}>
          <div className={cn({ 'container mx-auto': true })}>
            <PresaleNotice presale={bookingConfig.presale} status={presaleStatus} />
          </div>
        </div>
      )
    }
  }

  const stageId = stageIdOf(bookingConfig, stage)
  const stageComponent = stageId ? STAGE_COMPONENTS[stageId] : null

  return (
    <div className={styles.book}>
      <div className={cn({ 'container mx-auto': true })}>
        <div className={cn(styles.navContainer)}>
          <BookingNav stage={stage} onChange={actions.trackHistory} />
        </div>

        {state.loading && (
          <div className={cn(styles.loading)}>
            <div className={cn(styles.message)}>{state.loading.message}</div>
          </div>
        )}

        {state.error && state.error.scope !== 'local' && (
          <div className={cn(styles.error)}>{state.error.message}</div>
        )}

        {!state.loading && stageComponent}
      </div>
    </div>
  )
}
